import { useEffect, useState } from "react";
import api from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Checkbox } from "../components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { toast } from "sonner";
import { Syringe, Calculator, AlertTriangle, Loader2, Droplet } from "lucide-react";
import { CartridgeSize, useCartridgeSize } from "../components/CartridgeSize";

const CONDITION_FLAGS = [
  { id: "hypertension", label: "Cardiovascular disease / hypertension / arrhythmia" },
  { id: "pregnancy", label: "Pregnancy" },
  { id: "liver_disease", label: "Liver impairment" },
  { id: "renal_dialysis", label: "Renal impairment / dialysis" },
  { id: "comt_inhibitor", label: "Takes a COMT inhibitor (entacapone / opicapone — Parkinson's)" },
];

export default function Anesthetic() {
  const [options, setOptions] = useState([]);
  const [anesthetic, setAnesthetic] = useState("");
  const [weight, setWeight] = useState("");
  const [age, setAge] = useState("");
  const [cartridge, setCartridge] = useCartridgeSize();
  const [flags, setFlags] = useState([]);
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    api.get("/anesthetic/options").then(({ data }) => setOptions(data.anesthetics)).catch(() => {});
  }, []);

  const toggleFlag = (id) => setFlags((f) => (f.includes(id) ? f.filter((x) => x !== id) : [...f, id]));

  const calculate = async () => {
    if (!anesthetic) return toast.error("Select an anaesthetic");
    if (!weight || Number(weight) <= 0) return toast.error("Enter the patient's weight");
    setLoading(true);
    try {
      const { data } = await api.post("/anesthetic/calculate", {
        anesthetic_id: anesthetic,
        weight_kg: Number(weight),
        conditions: flags,
        cartridge_ml: cartridge,
        age: age ? Number(age) : null,
      });
      setResult(data);
    } catch {
      toast.error("Calculation failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="anesthetic-page">
      <div className="flex items-center gap-2.5">
        <div className="w-10 h-10 rounded-lg bg-sky-50 flex items-center justify-center">
          <Syringe className="w-5 h-5 text-sky-600" />
        </div>
        <div>
          <h1 className="font-heading text-2xl sm:text-3xl font-bold text-slate-900">Local Anaesthetic Calculator</h1>
          <p className="text-slate-500">Maximum safe dose &amp; adrenaline limit, adjusted to the patient.</p>
        </div>
      </div>

      <div className="grid lg:grid-cols-2 gap-6 mt-6">
        <div className="bg-white rounded-xl border border-slate-200 p-6 shadow-sm space-y-5">
          <div>
            <Label>Anaesthetic agent</Label>
            <Select value={anesthetic} onValueChange={setAnesthetic}>
              <SelectTrigger data-testid="anes-select" className="mt-1.5"><SelectValue placeholder="Select agent" /></SelectTrigger>
              <SelectContent>
                {options.map((o) => (
                  <SelectItem key={o.id} value={o.id} data-testid={`anes-${o.id}`}>{o.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label htmlFor="weight">Patient weight (kg)</Label>
            <Input id="weight" data-testid="anes-weight" type="number" value={weight} onChange={(e) => setWeight(e.target.value)} className="mt-1.5" placeholder="70" />
          </div>
          <div>
            <Label htmlFor="age">Patient age (years, optional)</Label>
            <Input id="age" data-testid="anes-age" type="number" value={age} onChange={(e) => setAge(e.target.value)} className="mt-1.5" placeholder="45" />
          </div>
          <CartridgeSize value={cartridge} onChange={(v) => { setCartridge(v); setResult(null); }} />
          <div>
            <Label className="mb-2 block">Patient factors</Label>
            <div className="space-y-2.5">
              {CONDITION_FLAGS.map((c) => (
                <label key={c.id} className="flex items-start gap-2.5 text-sm text-slate-700 cursor-pointer">
                  <Checkbox data-testid={`anes-flag-${c.id}`} checked={flags.includes(c.id)} onCheckedChange={() => toggleFlag(c.id)} className="mt-0.5" />
                  {c.label}
                </label>
              ))}
            </div>
          </div>
          <Button onClick={calculate} disabled={loading} data-testid="anes-calc-btn" className="w-full h-11 bg-sky-600 hover:bg-sky-700">
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Calculator className="w-4 h-4 mr-2" /> Calculate maximum dose</>}
          </Button>
        </div>

        <div>
          {!result ? (
            <div className="bg-white rounded-xl border border-dashed border-slate-300 p-10 text-center h-full flex flex-col items-center justify-center">
              <Calculator className="w-10 h-10 text-slate-300" />
              <p className="text-slate-500 mt-3">The maximum dose will appear here.</p>
            </div>
          ) : (
            <div data-testid="anes-result" className="space-y-4">
              <div className="bg-sky-50 border border-sky-200 rounded-xl p-6">
                <p className="text-sm text-sky-700">{result.anesthetic}</p>
                <p className="font-heading text-4xl font-bold text-slate-900 mt-1" data-testid="anes-max-cartridges">
                  {result.max_cartridges} <span className="text-lg font-normal text-slate-500">cartridges</span>
                </p>
                <p className="text-sm text-slate-600 mt-1">≈ {result.max_mg} mg maximum · {result.cartridge_ml} mL/cartridge</p>
                <p className="text-xs font-medium text-amber-700 mt-2 inline-flex items-center gap-1">
                  <Droplet className="w-3.5 h-3.5" /> Limited by: {result.limiting_factor}
                </p>
              </div>

              {result.adrenaline && (
                <div className="bg-white rounded-xl border border-slate-200 p-5 shadow-sm">
                  <h4 className="font-semibold text-slate-900 text-sm">Adrenaline (epinephrine)</h4>
                  <div className="grid grid-cols-2 gap-3 mt-3 text-sm">
                    <div><p className="text-slate-500">Max allowed</p><p className="font-medium text-slate-900">{result.adrenaline.limit_mg} mg {result.adrenaline.cardiac_restricted ? "(cardiac limit)" : ""}</p></div>
                    <div><p className="text-slate-500">Per cartridge</p><p className="font-medium text-slate-900">{result.adrenaline.per_cartridge_mg} mg</p></div>
                    <div><p className="text-slate-500">Max by adrenaline</p><p className="font-medium text-slate-900">{result.adrenaline.max_cartridges_by_adrenaline} cartridges</p></div>
                    <div><p className="text-slate-500">Max by drug dose</p><p className="font-medium text-slate-900">{result.max_cartridges_by_drug} cartridges</p></div>
                  </div>
                </div>
              )}

              {result.warnings?.length > 0 && (
                <div className="bg-amber-50 border border-amber-200 rounded-xl p-5">
                  <h4 className="font-semibold text-amber-900 text-sm flex items-center gap-1.5"><AlertTriangle className="w-4 h-4" /> Warnings</h4>
                  <ul className="mt-2 space-y-1.5">
                    {result.warnings.map((w, i) => <li key={i} className="text-sm text-amber-800">• {w}</li>)}
                  </ul>
                </div>
              )}

              {result.references?.length > 0 && (
                <div className="bg-white border border-slate-200 rounded-xl p-5" data-testid="anes-references">
                  <h4 className="font-semibold text-slate-900 text-sm">References</h4>
                  <ol className="mt-2 space-y-1 list-decimal pl-4">
                    {result.references.map((r) => <li key={r} className="text-xs text-slate-600">{r}</li>)}
                  </ol>
                </div>
              )}

              <p className="text-xs text-slate-400 italic">{result.disclaimer}</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
